import React, { createContext, useEffect, useState, useContext } from 'react';
import { requestNotificationPermission } from '../config/firebase-notification';
import { toast } from 'react-toastify';
import { UserContext } from './UserContext';
import api from '../api/api';
import { isSupported } from '../config/firebase-config';

export const NotificationContext = createContext();

// ✅ Retry mechanism with exponential backoff
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }

      const delay = baseDelay * Math.pow(2, attempt - 1);
      // Attempt failed, retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const NotificationProvider = ({ children }) => {
  const { user, token: authToken } = useContext(UserContext);
  const [fcmToken, setFcmToken] = useState(localStorage.getItem('fcm_token'));
  const [isRegistering, setIsRegistering] = useState(false);
  const [mountCount, setMountCount] = useState(0);

  // Track component mounts
  useEffect(() => {
    const newCount = mountCount + 1;
    setMountCount(newCount);
  }, []);

  useEffect(() => {
    if (!user?.isAuthenticated || !authToken) return;

    isSupported().then(async (supported) => {
      if (!supported) return;

      const storedToken = localStorage.getItem('fcm_token');

      if (!storedToken) {
        await registerToken();
      }
    });
  }, [user, authToken]);

  // ✅ Periodic token refresh (every 24 hours)
  useEffect(() => {
    if (!user?.isAuthenticated || !authToken) return;

    const refreshToken = async () => {
      try {
        await registerToken();
      } catch (error) {
        // Token refresh failed
      }
    };

    // Refresh token every 24 hours
    const refreshInterval = setInterval(refreshToken, 24 * 60 * 60 * 1000);

    // Also refresh when user becomes active after being idle
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.isAuthenticated) {
        const lastRefresh = localStorage.getItem('fcm_token_refresh_time');
        const now = Date.now();
        const hoursSinceRefresh = (now - (lastRefresh || 0)) / (1000 * 60 * 60);

        if (hoursSinceRefresh > 24) {
          refreshToken();
          localStorage.setItem('fcm_token_refresh_time', now);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, authToken]);

  const registerToken = async () => {
    if (isRegistering) return; // Prevent multiple simultaneous registrations

    setIsRegistering(true);

    try {
      // ✅ Force clear old token and service worker cache
      const oldToken = localStorage.getItem('fcm_token');
      if (oldToken) {
        localStorage.removeItem('fcm_token');

        // Force service worker to update
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
              await registration.update();
            }
          } catch (error) {
            // Service worker update failed
          }
        }
      }

      const token = await retryWithBackoff(async () => {
        const newToken = await requestNotificationPermission();
        if (!newToken) {
          throw new Error('Failed to get notification permission');
        }
        return newToken;
      });

      const storedToken = localStorage.getItem('fcm_token');
      if (storedToken === token) {
        return;
      }

      // ✅ Retry API call with backoff
      await retryWithBackoff(async () => {
        const response = await api.post('manage-fcm-token', {
        token: token,
        action: 'add',
        });

        if (!response.data?.token) {
          throw new Error('Failed to register token with server');
        }

        return response;
      });

      setFcmToken(token);
      localStorage.setItem('fcm_token', token);
      localStorage.setItem('fcm_token_refresh_time', Date.now());

    } catch (err) {
      // Token registration failed

      // Show user-friendly error message
      if (err.message.includes('VAPID key')) {
        toast.error('Notification setup failed. Please contact support.');
      } else if (err.message.includes('Permission denied')) {
        toast.warning('Please enable notifications to receive updates.');
      } else {
        toast.error('Failed to setup notifications. Please try again later.');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  useEffect(() => {
    let handleSWMessage;

    isSupported().then((supported) => {
      if (!supported) return;

      handleSWMessage = (event) => {
        // Auth check yahan karo
        if (!user?.isAuthenticated || !authToken) return;
        if (event.data?.type === 'NEW_NOTIFICATION') {
          const { title, body } = event.data.payload.notification;
          const alertLevel = event.data.payload.data?.alert_level || 'info';
          // Choose the toast function based on alertLevel
          let showToast;
          if (alertLevel === 'danger') {
            showToast = toast.error;
          } else if (alertLevel === 'warning') {
            showToast = toast.warning;
          } else if (alertLevel === 'success') {
            showToast = toast.success;
          } else {
            showToast = toast.info;
          }

          showToast(
            <>
              <strong>{title}</strong>
              <div>{body}</div>
            </>
          );

          const audio = new Audio('/notif.mp3');
          audio.play().catch(err => {
            // Sound error
          });

          window.dispatchEvent(new Event('new-notification'));
        }
      };

      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    });

    // Cleanup on unmount
    return () => {
      if (handleSWMessage) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
    // eslint-disable-next-line
  }, []);


  // useEffect(() => {
  //   if (!user?.isAuthenticated || !authToken) return;

  //   isSupported().then((supported) => {
  //     if (!supported) return;

  //     const handleSWMessage = (event) => {
  //       if (event.data?.type === 'NEW_NOTIFICATION') {
  //         const { title, body } = event.data.payload.notification;

  //         toast.info(
  //           <>
  //             <strong>{title}</strong>
  //             <div>{body}</div>
  //           </>
  //         );

  //         const audio = new Audio('/notif.mp3');
  //         audio.play().catch(err => console.warn('🔇 Sound error:', err.message));
  //       }
  //     };

  //     navigator.serviceWorker.addEventListener('message', handleSWMessage);

  //     return () => {
  //       navigator.serviceWorker.removeEventListener('message', handleSWMessage);
  //     };
  //   });
  // }, [user, authToken]);

  return (
    <NotificationContext.Provider value={{ fcmToken, registerToken }}>
      {children}
    </NotificationContext.Provider>
  );
};

export { NotificationProvider };
